import { WorkCalendarHelper } from "@cervval.js/calendars/src/calendars.helper";
import { ItemValueOrFunction } from "@cervval.js/common/src/tools/object.tools";
import { SECONDS_IN_ONE_HOUR, SECONDS_IN_ONE_YEAR } from "@cervval.js/common/src/tools/time.tools";
import { TsSimpleDuration, UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { AgendaCommonOptions } from "../../agenda.types";
import { CjsDayAgendaColumnView, DayAgendaColumnViewData, DayAgendaColumnViewOptions } from "../dayAgendaColumnView/dayAgendaColumnView";
import { AgendaNavigationOptions } from "../dayAgendaNavigation/dayAgendaNavigation.helper";
import { DayAgendaItemContext } from "./dayAgendaContext";
import { DayAgendaModel } from "./dayAgendaModel";
import { defaultItemRender } from "./dayAgendaUi.helper.items";

export interface DayAgendaOptions<ItemObject, ColumnObject> extends AgendaNavigationOptions, AgendaCommonOptions {

    /** when a workCalendarHelper is set, non worked hours are not displayed */
    workCalendarHelper?: WorkCalendarHelper;

    /** Initial location of split pane divider */
    initLeftPanelPercentage: number;

    /** Css style of borders for splitPane and table */
    borderCssStyle: string;

    /** row min height */
    rowHeight: number;// TODO move in AgendaColOptions

    /** Col options */
    defaultColOptions: AgendaColumnOptions<ItemObject, ColumnObject>;// TODO rename rowOptions

    /** Partial style of the item when it is being created  */
    newItemStyle: Partial<AgendaItemStyle>;

    /**
     * Magnetism is used to adjust a given timestamp to the closest time step defined by a duration. It is typically used on item edition (move or resize) or creation. 
     * Every time the item's timestamps are meant to change when interacting with the agenda
     * E.g : with a step (duration) of 2 days ({days:2}), the output timestamp can only be every 2 days, starting at the beginning of the month.
     * See MagnetismOption type for more details.
     */
    magnetismOptions: MagnetismOptions<ItemObject, ColumnObject>;

    /** Disable zoom in/out on ctrl + scroll */
    disableZoom: boolean;

    /** Disable pan move on middle click mousemove */
    disablePanMove: boolean;
    /** If true, the agenda won't refresh automatically on interactionRefreshEvent (intermediate refresh before interactionFinishEvent)  */
    disableAutoRefreshOnInteractionRefreshEvents?: boolean;
    /** If true, the agenda won't refresh automatically on interactionFinishEvent */
    disableAutoRefreshOnInteractionFinishEvents?: boolean;

    /** Disable navigation buttons */
    dragMode: AgendaDragMode;

    /** 
     * If true, on each interaction refresh, the smart table is refreshed as well even if first row has not changed
     */
    refreshSmartTableDuringInteractions: boolean;

    /** 
     * If true, the agenda does not refresh the items on user interactions since the page will call refresh.
     * This option is usually true when agenda is associated to an alert model since the render needs to be refreshed after alert computation is done
     */
    appWillRefreshAgendaAfterAction?: boolean;

    /** If true, items are created with right button of mouse */
    createItemsOnRightClick?: boolean;

    /**
     * If true, a cursor is added on agenda rows
     * Cursor is used to select or display a unique dateTime in agenda
     * You should define cursor interactions callbacks
     */
    enableCursor?: boolean;

    /** If true, the row in the agenda and the smartTable under the cursor are hover. */
    enableHover?: boolean;

    /**
     * Override logMessage for action in agenda
     */
    logMessage?: {

        /**
         * @returns the formatted log message or undefined to keep the default message
         */
        formatMessage?: (items: ItemObject[], modification: "create" | "delete" | "move" | "resize_start" | "resize_finish") => string | undefined,
        /**
         * @returns the name of the item to use in default logMessage (useless if you have overridden formatMessage)
         */
        getItemName?: (item: ItemObject) => string,
        /**
         * @returns the name of the row to use in default logMessage (useless if you have overridden formatMessage)
         */
        getColName?: (row: ColumnObject) => string
    };

    /** Disable mode selector */
    disableModeSelector?: boolean;
}

/**
 * Mode of drag&drop in agenda interactions
 * x: only move item horizontally
 * y: only move item vertically
 * oneAxis: only move item on x axis or y axis in one drag & drop
 * both: move item vertically and horizontally (all directions)
 */
export type AgendaDragMode = "x" | "y" | "oneAxis" | "both";

export interface AgendaColumnOptions<ItemObject = anyReallyAny, ColumnObject = anyReallyAny> {
    borderWidth: number;
    itemRender: (agendaModel: DayAgendaModel<ItemObject, ColumnObject>, itemContext: DayAgendaItemContext<ItemObject>, mainDiv: HTMLElement, innerDiv: HTMLElement) => void;
    defaultItemStyle: AgendaItemStyle;
    /** build custom rows */
    columnViewFactory?: (data?: DayAgendaColumnViewData<ItemObject, ColumnObject>, options?: Partial<DayAgendaColumnViewOptions>) => CjsDayAgendaColumnView<ItemObject, ColumnObject> | undefined;
}

export interface AgendaItemStyle {
    borderRadius: number | undefined;
    borderWidth: number | undefined;
    borderStyle: "none" | "dotted" | "dashed" | "solid" | undefined;
    borderColor: string | undefined;
    background: string | undefined;
    selectedBackground: string | undefined;
    selectedBorder: string | undefined;
    selectedHandleColor: string;
    textColor: string | undefined;
    stripe: boolean;

    /** won't be displayed by default parameters as stripe is set to false */
    stripeColor: string;

    /** Corresponds to the color of the resize item's handle when hovered */
    handleHoverColor: string;
}

/** 
 * Magnetism is used to adjust a given timestamp to the closest time step defined by a duration. It is typically used on item edition (move or resize) or creation. 
 * Every time the item's timestamps are meant to change when interacting with the agenda
 * E.g : with a step (duration) of 2 days ({days:2}), the output timestamp can only be every 2 days, starting at the beginning of the month.
 * 
 * ! magnetismFunc will be prioritized and used if defined.
 * To compute the new start/finish timestamp, the most constraining step will be used so the biggest duration set.
 * If no option is defined, there won't be any magnetism and the timestamp will not be transformed
 */
export type MagnetismOptions<ItemObject, ColumnObject> = {
    /**
     * Custom transformation of a timestamp.
     * If defined, the function magnetismFunc is used to adjust any timestamp.
     * Else the biggest step defined will be effective.
     * When moving an item, the biggest between the row, item start timestamp and item finish timestamp step will be used.
     * When resizing or creating an item, to adjust the start timestamp the most constraining between the row and the item start timestamp step is used. The same method is used to adjust the finish timestamp
     */
    magnetismFunc?: (ts: UnixTimestamp, item: ItemObject | null, row: ColumnObject) => UnixTimestamp,
    /** Step duration used on the item start timestamp. Can be a function returning a duration : (item)=>Duration */
    itemStartTsMagnet?: ItemValueOrFunction<ItemObject, TsSimpleDuration>,
    /** Step duration used on the item finish timestamp. Can be a function returning a duration : (item)=>Duration */
    itemFinishTsMagnet?: ItemValueOrFunction<ItemObject, TsSimpleDuration>,
    /** Step duration used on any item timestamp on the row. Can be a function returning a duration : (item)=>Duration  */
    colMagnet?: ItemValueOrFunction<ColumnObject, TsSimpleDuration>
}

const DEFAULT_ITEM_BACKGROUND = "#1a73e8";
const DEFAULT_ITEM_BORDER = "#66b0e4";
const DEFAULT_SELECTED_ITEM_BACKGROUND = "#62c762";
const DEFAULT_SELECTED_ITEM_BORDER = "#4c9a4c";

export const defaultDayAgendaOptions: DayAgendaOptions<anyReallyAny, anyReallyAny> = {// Any allowed, we can't type generic types in default options
    /** Initial location of split pane divider */
    initLeftPanelPercentage: 20,

    /** Css style of borders for splitPane and table */
    borderCssStyle: "1px solid #ddd",

    rowHeight: 50,
    enableHover: false,
    defaultColOptions: {
        borderWidth: 1,
        itemRender: defaultItemRender,
        defaultItemStyle: {
            borderRadius: 5,
            borderWidth: 0,
            borderStyle: "solid" as "none" | "dotted" | "dashed" | "solid",
            borderColor: DEFAULT_ITEM_BORDER,
            background: DEFAULT_ITEM_BACKGROUND,
            textColor: "white",
            stripe: false,
            stripeColor: "white", // won't be displayed by default parameters as stripe is set to false
            handleHoverColor: DEFAULT_ITEM_BORDER,
            selectedBackground: DEFAULT_SELECTED_ITEM_BACKGROUND,
            selectedBorder: DEFAULT_SELECTED_ITEM_BORDER,
            selectedHandleColor: DEFAULT_SELECTED_ITEM_BORDER
        }
    },

    newItemStyle: { background: "green" },

    /** Magnetism is active by default  */
    magnetismOptions: {
        colMagnet: { unit: "minute", unitCount: 30 },
        itemStartTsMagnet: { unit: "minute", unitCount: 30 },
        itemFinishTsMagnet: { unit: "minute", unitCount: 30 }
    },

    /** By default, we have 2 agendaTimescales with automatic unit computation */
    agendaHeaderOptions: [

    ],

    /** Zoom in/out on ctrl + scroll is enabled by default */
    disableZoom: false,
    /** Min / max zoom level (number of seconds between start and finish) */
    minZoomLevelInSeconds: SECONDS_IN_ONE_HOUR * 4,
    maxZoomLevelInSeconds: SECONDS_IN_ONE_YEAR * 1.5,
    /** Debounce time in MS for zoom (min time between 2 zoom refresh) */
    zoomDebounceTime: 300,
    /** Zoom scale divider */
    zoomScaleDivider: 8,

    /** Pan move on middle click is enabled by default */
    disablePanMove: false,
    /** Debounce time in MS for pan mousemove event (min time between 2 pan refresh) */
    panMoveDebounceTime: 100,

    /** By default, we don't refresh smart table during interactions */
    refreshSmartTableDuringInteractions: false,

    /** Navigation buttons are enabled by default */
    disableNavigationButtons: false,

    /** Drag&Drop mode */
    dragMode: "oneAxis",

    enableCursor: true
};